import { usePage } from "@inertiajs/react"
import { PostHogProvider, usePostHog } from "@posthog/react"
import type { ReactNode } from "react"
import { useEffect, useRef } from "react"

import type { SharedData } from "@/types"

type PostHogConfig = NonNullable<SharedData["posthog"]>

interface AppPostHogProviderProps {
  children: ReactNode
  config?: PostHogConfig | null
}

const isConfigured = (
  config?: PostHogConfig | null,
): config is PostHogConfig & { projectApiKey: string } =>
  !!config?.enabled && !!config.projectApiKey

export function PostHogIdentityLifecycle() {
  const posthog = usePostHog()
  const page = usePage<SharedData>()
  const identifiedUserId = useRef<string | null>(null)
  const user = page.props.auth?.user

  useEffect(() => {
    if (!user) {
      if (identifiedUserId.current) {
        posthog.reset()
        identifiedUserId.current = null
      }
      return
    }

    const distinctId = String(user.id)

    if (identifiedUserId.current === distinctId) {
      return
    }

    posthog.identify(distinctId, {
      email: user.email,
      name: user.name,
      role: user.role,
      handle: user.handle,
      has_active_subscription: user.has_active_subscription,
      created_at: user.created_at,
    })
    identifiedUserId.current = distinctId
  }, [posthog, user])

  return null
}

export default function AppPostHogProvider({
  children,
  config,
}: AppPostHogProviderProps) {
  if (!isConfigured(config)) {
    return children
  }

  return (
    <PostHogProvider
      apiKey={config.projectApiKey}
      options={{
        api_host: config.apiHost ?? "https://us.i.posthog.com",
        ui_host: config.uiHost ?? "https://us.posthog.com",
        defaults: config.defaults ?? "2026-01-30",
        mask_all_element_attributes: true,
        mask_all_text: true,
        person_profiles: "identified_only",
      }}
    >
      {children}
    </PostHogProvider>
  )
}
